import {
	checkHasApprovalHierarchy,
	compareStrings,
	disabledByAnotherAction,
	getCurrency,
	getObjectValues,
	getWorkOrderTimeZoneId,
	highestTierSplitPartiallyApproved,
	isHierarchyNTEBased,
	isSupplierPayingCustomer,
	nullSafeGet,
	nullSafeGetOrElse,
	recordHasContractorId,
	roundOffToDecimal,
} from '../../utils/DataAccessUtils';
import * as Sentry from '@sentry/react';
import { Button, Card, Col, message, Popover, Row } from 'antd';
import moment from 'moment';
import * as React from 'react';
import { useMemo, useRef, useState } from 'react';
import ApprovalHierarchyDisplayQuoteModal from './ApprovalHierarchyDisplayQuoteModal';
import DeclineQuoteForm from '../decline_quote_form/DeclineQuoteForm';
import { connect } from 'react-redux';
import { UserName } from '../name_component/user/UserNameComponent';
import { CURRENCIES, ROLE_TYPES } from '../../utils/DataConstants';
import {
	isAllowedToPerform,
	isBuyerOrInternalTech,
	isInternalTech,
	isSupplierAllowedToAccess,
	isThirdPartySupplier,
	PERMISSION_NAMES,
} from '../../utils/AuthUtils';
import { checkSequentialApprovalBlock } from '../../utils/ApprovalHierarchyUtils';
import ForwardApproval from '../forward_approval/ForwardApproval';
import {
	forwardApprovalToUserForBuyer,
	forwardApprovalToUserForSupplier,
} from '../../thunks/company_approval_thunks';
import { QUOTE_STATUSES } from '../../constants/QuoteStatuses';
import BudgetChart from '../charts/BudgetChart';
import _ from 'lodash';
import { infinityButNumber } from '../../constants/general';
import { joinListElements, STOPLIGHT_COLORS } from '../../utils/DataFormatterUtils';
import {
	proposalStatusChangeForBuyer,
	proposalStatusChangeForClientContractor,
	proposalStatusChangeForContractor,
	proposalStatusChangeForInternalSupplier,
	proposalStatusChangeForSupplier,
} from '../../thunks/proposals_thunks';
import { isClientQuoteView } from '../quote_utils/QuoteUtils';
import { isManagedBuyerCompanyIdWO } from '../../utils/WorkOrderUtils';
import ViewPastRevisionsButton from '../view_past_revisions_button/ViewPastRevisionsButton';
import { PublishQuoteBanner } from '../publish_quote_banner/PublishQuoteBanner';
import { QuoteMoveBackToDraftButton } from '../quote_move_back_to_draft_button/QuoteMoveBackToDraftButton';
import DateTimeHover from '../date_time_component/DateTime';
import { FINISHED_WORK_ORDER_DISPLAY_STATUSES } from '../../constants/WorkOrderDisplayStatuses';

function ApprovalHierarchyDisplayQuote({
	quote,
	currentUser,

	markDraftQuote,
	markPendingQuote,
	approveQuote,
	declineQuote,

	syncQuote,
	appendWorkOrderNotes,
	refresh,
	style = {},
	allowedToApprove = true,

	history,
	companyConfig,
	userType,

	forwardApprovalToUser,

	approveQuoteContractor,
	declineQuoteContractor,
	pendingQuoteContractor,
}) {
	const isContractorQuote = recordHasContractorId(quote);
	const workOrder = useMemo(() => nullSafeGetOrElse('workOrder', quote, {}), [quote]);
	const isSubcontractorView = useMemo(
		() => !isBuyerOrInternalTech(currentUser) && !isClientQuoteView(quote, currentUser),
		[quote, currentUser]
	);
	const isSupplierManagedBuyer = useMemo(
		() =>
			(nullSafeGet('buyerCompanyId', workOrder) === -100 &&
				nullSafeGet('subContractedToWorkOrder.contractorSupplierFacilityId', workOrder) ===
					nullSafeGet('primaryFacilityId', currentUser)) ||
			isManagedBuyerCompanyIdWO(workOrder, currentUser),
		[workOrder, currentUser]
	);

	const canShowPayingSupplierApprove = useMemo(
		() => isSubcontractorView || (isSupplierPayingCustomer(currentUser) && isSupplierManagedBuyer),
		[isSubcontractorView, currentUser, isSupplierManagedBuyer]
	);
	/*
		Show Draft Status When (or):
			1. Normal is third party supplier
			2. Not the subcontractor view = is my quote and not my subcontractors quote
			3.
	 */
	const canShowDraftStatus = useMemo(() => !isSubcontractorView, [isSubcontractorView]);
	const defaultActionsAllowed =
		userType === ROLE_TYPES.SUPPLIER
			? isSupplierAllowedToAccess(
					PERMISSION_NAMES.CAN_APPROVE_QUOTES,
					companyConfig,
					currentUser.roles
			  )
			: isAllowedToPerform('canApproveQuotes', companyConfig, currentUser.roles);
	const [showModal, setShowModal] = useState(false);
	const [actionButtonLoading, setActionButtonLoading] = useState(null);
	const [showDeclineModal, setShowDeclineModal] = useState(false);
	const status = nullSafeGet('status', quote);
	// A voided quote (proposal status `expired`) can be revived once its work order has been
	// reopened — i.e. the work order is no longer in a finished (Completed/Cancelled) state.
	// Quotes voided for other terminal reasons (e.g. invoice approval) keep their work order
	// finished, so this gate naturally excludes them.
	const workOrderDisplayStatus = nullSafeGet('displayStatus', workOrder);
	const canMoveVoidedQuoteToPending =
		compareStrings(status, QUOTE_STATUSES.expired) &&
		workOrderDisplayStatus &&
		!FINISHED_WORK_ORDER_DISPLAY_STATUSES.includes(workOrderDisplayStatus);
	const reason = useRef();
	const setReason = (str) => (reason.current = str);
	const showStatus = (status) => {
		switch (status) {
			case 'pending':
				return 'Pending Approvers';
			case 'declined':
				return 'Approvers/Disputers';
			case 'awarded':
				return 'Approvers';
			case 'voided':
				return 'Approver List';
			default:
				return 'Approver List';
		}
	};
	const hasApprovalHierarchy = checkHasApprovalHierarchy(quote);
	const sequentialApprovalEnforcement = nullSafeGetOrElse(
		'approvalHierarchy.sequentialApprovalEnforcement',
		quote,
		false
	);
	const userTier = (tiers) =>
		hasApprovalHierarchy
			? tiers.filter((tier) =>
					tier.approvers.some((approver) => compareStrings(approver.email, currentUser.email))
			  ).length > 0
				? tiers.find((tier) =>
						tier.approvers.some((approver) => compareStrings(approver.email, currentUser.email))
				  )
				: undefined
			: tiers[0];

	const currency = getCurrency({ workOrder: nullSafeGet('workOrder', quote) });
	const locationCurrency =
		CURRENCIES[nullSafeGetOrElse('workOrder.locationCurrencyId', quote, 'USD')];
	const buyerCompanyCurrency = getCurrency({
		buyerCompany: nullSafeGet('workOrder.buyerCompany', quote),
	});
	const exchangeRate =
		currency.id === buyerCompanyCurrency.id
			? 1
			: nullSafeGetOrElse('workOrder.currencyExchangeRateLocationToSupplier', quote, 1) *
			  nullSafeGetOrElse('workOrder.currencyExchangeRateBuyerToLocation', quote, 1);
	let quoteTotal = nullSafeGet('totalAfterTax', quote) / exchangeRate;
	let lessThanQuote = true;
	let highestValidAmount = 0;
	let highestApprovedAmount = 0;
	const highestApprovalTierAmount = (tiers) => {
		let highestTiers = [...tiers]
			.filter((tier) => {
				let approvedByUsers = nullSafeGet('approvedByUsers', tier);
				let declinedByUsers = nullSafeGet('declinedByUsers', tier);
				if (approvedByUsers && declinedByUsers) {
					return approvedByUsers.concat(declinedByUsers).length > 0;
				} else {
					return false;
				}
			})
			.map((tier) => {
				return nullSafeGetOrElse('amountUpto', tier, infinityButNumber);
			});
		if (highestTiers.length > 0) {
			return highestTiers[highestTiers.length - 1];
		}
		return 0;
	};

	let lastAmount = 0;
	const isNTEBased = isHierarchyNTEBased(
		nullSafeGetOrElse('approvalHierarchy.tierDetails', quote, [])
	);
	const tiers = nullSafeGet('approvalHierarchy.tierDetails', quote)
		? nullSafeGet('approvalHierarchy.tierDetails', quote)
				//Sort tiers primarily by tier.amountUpto, secondarily by tier.label
				.sort((a, b) =>
					a.amountUpto && b.amountUpto
						? parseFloat(a.amountUpto) > parseFloat(b.amountUpto)
							? 1
							: parseInt(a.label) > parseInt(b.label)
							? 1
							: -1
						: a.amountUpto === undefined
						? 1
						: -1
				)
				//Setup tier objects
				.map((tier) => {
					if (nullSafeGetOrElse('approvedByUsers', tier, []).length > 0) {
						highestApprovedAmount = parseInt(nullSafeGet('amountUpto', tier)) || Infinity;
					}
					return {
						...tier,
						approvedByUsers: nullSafeGetOrElse('approvedByUsers', tier, []).filter(
							(user, i, users) => i === users.findIndex((u) => compareStrings(u.email, user.email))
						),
						declinedByUsers: nullSafeGetOrElse('declinedByUsers', tier, []).filter(
							(user, i, users) => i === users.findIndex((u) => compareStrings(u.email, user.email))
						),
					};
				})
				.map((tier, i, tiers) => {
					let message;
					let isVisible = () => {
						if (!isNTEBased) {
							//We want to see all tiers when it is not NTE Based
							return true;
						}
						//We also want to display tiers which fit one of the following criteria:
						let amount = parseInt(nullSafeGet('amountUpto', tier));

						//1.This tier has either approvers or decliners and is >= the highest amount approved
						if (
							highestApprovedAmount > amount &&
							tier.approvedByUsers.length === 0 &&
							tier.declinedByUsers.length === 0
						) {
							//Not shown because a higher tier already approved
							message = 'A higher tier has already approved';
							return false;
						}
						if (
							highestApprovedAmount === amount &&
							tier.approvers.length === 0 &&
							tier.approvedByUsers.length === 0
						) {
							//Not shown because an equal tier has already approved and this tier has no approvers
							message =
								'An equal tier has approved and this tier has no approvers for this location';
							return false;
						}
						if (!lessThanQuote) {
							let lastAmount = parseInt(nullSafeGetOrElse('amountUpto', tiers[i - 1], 0));
							//3. This tier has already been approved or declined
							if (tier.approvedByUsers.length > 0 || tier.declinedByUsers.length > 0) {
								return true;
							} else {
								//4. This tier is the same amount as the previous tier
								if (highestValidAmount === amount && highestValidAmount === lastAmount) {
									return true;
								} else {
									message = `Approval from this tier isn't required for quotes less than ${currency.format(
										(lastAmount ? lastAmount : amount) * exchangeRate
									)}`;
									return false;
								}
							}
						}

						if (amount && quoteTotal) {
							if (parseFloat(amount) > parseFloat(quoteTotal)) {
								//2. This tier is one tier over the quote and contains potential approvers
								highestValidAmount = amount;
								if (nullSafeGetOrElse('approvers.length', tier, 0) > 0) {
									lessThanQuote = false;
								}
							}
						} else {
							//1. This tier is less than the quote
							lessThanQuote = false;
						}
						return true;
					};
					lastAmount =
						nullSafeGetOrElse('amountUpto', tiers[i - 1], 0) === nullSafeGet('amountUpto', tier)
							? lastAmount
							: nullSafeGetOrElse('amountUpto', tiers[i - 1], 0);
					return { ...tier, isVisible: isVisible(), message, lastAmount };
				})
		: [];
	const hasApprovers = (tiers) =>
		tiers.some((tier) => nullSafeGetOrElse('approvers', tier, []).length > 0);
	const showApproval = [QUOTE_STATUSES.pending, QUOTE_STATUSES.disputed].some((status) =>
		compareStrings(status, quote.status)
	);
	const highestAmount = highestApprovalTierAmount(tiers);
	let myTier = userTier(tiers);
	const myTierAmount = nullSafeGet('amountUpto', myTier);

	// this condition is because of concurrency on workorder status , where a user updates something on the stale state.
	if (
		highestApprovedAmount > quoteTotal &&
		!highestTierSplitPartiallyApproved(tiers, highestApprovedAmount) &&
		showApproval
	) {
		// the highest approved amount is greater than the nte
		// and the work order is still pending. there is some bug. so we will reset the tiers so that nobody has approved.
		Sentry.captureException(`Error state for Quote: Resetting Approval Hierarchy ${quote.id}`);
		tiers.forEach((tier) => {
			tier.approvedByUsers = [];
			tier.declinedByUsers = [];
		});
	}

	const currentQuote = {
		...quote,
		approvalHierarchy: { ...quote.approvalHierarchy, tierDetails: tiers },
		highestAmount: highestAmount,
	};
	const allowApproval = useMemo(() => {
		if (status !== 'awarded') {
			if (compareStrings(nullSafeGet('workOrder.displayStatus', quote), 'Pending')) {
				setReason(`Work Order is still pending, quote can't be processed yet.`);
				return false;
			}
			if (hasApprovalHierarchy && myTier !== undefined) {
				if (sequentialApprovalEnforcement) {
					const seqCheck = checkSequentialApprovalBlock(tiers, myTier);
					if (seqCheck.blocked) {
						setReason(seqCheck.reason);
						return false;
					}
				}
				if (myTierAmount === undefined || parseFloat(myTierAmount) >= highestAmount) {
					if (
						!nullSafeGetOrElse('approvedByUsers', myTier, []).some((user) =>
							compareStrings(currentUser.email, user.email)
						)
					) {
						return true;
					} else {
						setReason('Your tier has already approved.');
						return false;
					}
				}
			} else if (!hasApprovalHierarchy) {
				//No approval Hierarchy , default to access control
				return defaultActionsAllowed;
			}
			setReason('You are not in the approval hierarchy.');
		} else {
			setReason('This quote has already been approved.');
		}
		return false;
	}, [
		status,
		quote,
		currentUser.email,
		hasApprovalHierarchy,
		myTier,
		myTierAmount,
		highestAmount,
		defaultActionsAllowed,
		sequentialApprovalEnforcement,
		tiers,
	]);
	const allowDecline = useMemo(() => {
		if (compareStrings(nullSafeGet('workOrder.displayStatus', quote), 'Pending')) {
			setReason(`Work Order is still pending, quote can't be processed yet.`);
			return false;
		}
		if (status !== 'awarded' && status !== 'declined') {
			if (hasApprovalHierarchy && myTier) {
				if (sequentialApprovalEnforcement) {
					const seqCheck = checkSequentialApprovalBlock(tiers, myTier);
					if (seqCheck.blocked) {
						setReason(seqCheck.reason);
						return false;
					}
				}
				if (myTierAmount === undefined || parseFloat(myTierAmount) >= highestAmount) {
					if (
						!nullSafeGetOrElse('approvedByUsers', myTier, []).some((user) =>
							compareStrings(currentUser.email, user.email)
						)
					) {
						return true;
					} else {
						setReason('Your tier has already approved.');
						return false;
					}
				}
			} else if (!hasApprovalHierarchy && myTier !== undefined) {
				if (
					nullSafeGetOrElse('approvedByUsers', myTier, []).some((user) =>
						compareStrings(currentUser.email, user.email)
					)
				) {
					setReason('You have already approved.');
					return false;
				} else if (nullSafeGetOrElse('approvedByUsers', myTier, []).length > 1) {
					setReason('Someone has already approved.');
					return false;
				} else {
					setReason(`You are not allowed to approve/dispute quote.`);
					return defaultActionsAllowed;
				}
			} else if (!hasApprovalHierarchy && myTier === undefined) {
				setReason(`You are not allowed to approve/dispute quote.`);
				return defaultActionsAllowed;
			}
		}
		if (status === 'declined') {
			setReason('This quote has already been declined.');
		}
		return false;
	}, [
		status,
		hasApprovalHierarchy,
		myTier,
		myTierAmount,
		highestAmount,
		defaultActionsAllowed,
		sequentialApprovalEnforcement,
		tiers,
	]);

	const handleMoveBackToPendingQuote = (quote) => {
		message.open({
			key: 'quoteDetailMessageUpdating',
			type: 'loading',
			content: 'Moving back to pending...',
			duration: 60,
		});
		setActionButtonLoading(QUOTE_STATUSES.pending);
		markPendingQuote(quote, isContractorQuote)
			.then((resultQuote) => {
				message.destroy('quoteDetailMessageUpdating');
				message.success('Moved back to pending!');
				refresh();
			})
			.finally(() => {
				setActionButtonLoading(null);
			});
	};

	const handleApproveQuote = (quote) => {
		let myTier = userTier(tiers)
			? userTier(tiers)
			: {
					approvers: [],
					approvedByUsers: [],
					declinedByUsers: [],
					approvedByType: 'any',
					label: '1',
			  };

		//Add this user to approvedByUsers list
		myTier.approvedByUsers.push({
			email: currentUser.email.toLowerCase(),
			approverType: 'user',
			contact: currentUser,
		});
		let newquote = {
			...quote,
			approvalHierarchy: {
				...quote.approvalHierarchy,
				tierDetails: quote.approvalHierarchy.tierDetails.map((tier) => {
					return tier.id === myTier.id ? { ...myTier } : tier;
				}),
			},
		};
		setActionButtonLoading(QUOTE_STATUSES.approved);
		message.open({
			key: 'quoteDetailMessageUpdating',
			type: 'loading',
			content: 'Approving...',
			duration: 60,
		});
		approveQuote(newquote).then((resultQuote) => {
			setActionButtonLoading(null);
			message.destroy('quoteDetailMessageUpdating');
			message.success('Approved!');
			refresh();
		});
	};
	const handleDisputeQuote = (quote) => {
		setShowDeclineModal(true);
	};
	const handleSubmitDisputeQuote = (values) => {
		let myTier = userTier(tiers)
			? userTier(tiers)
			: {
					approvers: [],
					approvedByUsers: [],
					declinedByUsers: [],
					approvedByType: 'any',
					label: '1',
			  };

		//Add this user to declining user list
		myTier.declinedByUsers.push({
			email: currentUser.email.toLowerCase(),
			approverType: 'user',
			contact: currentUser,
		});

		//If this user is currently approving, remove them from approving list
		myTier.approvedByUsers = myTier.approvedByUsers.filter((user) => {
			return user.email.toLowerCase() !== currentUser.email.toLowerCase();
		});
		const note = {
			text: 'Quote Declined: '.concat(values.note),
			noteAddedBy: nullSafeGet('email', currentUser),
			noteAddedAt: moment.utc(),
		};
		const workOrderNoteDetails = {
			id: nullSafeGet('workOrder.id', quote),
			locationId: nullSafeGet('workOrder.locationId', quote),
			note,
		};

		let newquote = {
			...currentQuote,
			approvalHierarchy: {
				...currentQuote.approvalHierarchy,
				tierDetails: currentQuote.approvalHierarchy.tierDetails.map((tier) => {
					return tier.id === myTier.id ? { ...myTier } : tier;
				}),
			},
			declineNote: {
				text: values.note,
				noteAddedBy: nullSafeGet('email', currentUser),
				noteAddedAt: moment.utc(),
			},
		};

		setActionButtonLoading(QUOTE_STATUSES.disputed);
		declineQuote(newquote).then((resultQuote) => {
			appendWorkOrderNotes(workOrderNoteDetails).then(() => {
				message.success(`Quote declined.`);
				setShowDeclineModal(false);
				setActionButtonLoading(null);
				refresh();
			});
		});
	};
	const contactType = nullSafeGetOrElse('contactType', currentUser, 'buyer');
	const isInternalTech = nullSafeGetOrElse('facility.status', currentUser, '') === 'private';

	const lastDeclineNote = currentQuote.declineNotes && currentQuote.declineNotes.slice(-1)[0];
	const declineNote = lastDeclineNote ? (
		<Col className={'pb-4'}>
			<div>
				<span style={{ fontSize: 16 }}>
					Declined by{' '}
					<UserName mode={'inline'} contact={nullSafeGet(`noteAddedByContact`, lastDeclineNote)} />{' '}
					{moment(nullSafeGet(`noteAddedAt`, lastDeclineNote)).fromNow()}
				</span>
			</div>
			<div
				style={{
					display: 'inline-flex',
					background: '#fafafa',
					color: 'rgba(0, 0, 0, 0.65)',
					borderRadius: '30px',
					padding: '8px 16px',
				}}
			>
				<span
					style={{
						fontSize: 16,
					}}
				>
					{nullSafeGet(`text`, lastDeclineNote)}
				</span>
			</div>
			{contactType === 'supplier' && !isInternalTech ? (
				<div className={'mt-2 flex gap-4'}>
					<Button
						type="primary"
						key="Edit"
						onClick={() => history.push(`/supplier/quotes/edit/${currentQuote.id}`)}
					>
						Edit Quote
					</Button>
					<Button
						key="Note"
						onClick={() =>
							history.push(`/supplier/workOrders/detail/${currentQuote.workOrderId}/notes`)
						}
					>
						Send a note
					</Button>
				</div>
			) : null}
		</Col>
	) : null;

	const isExternal = contactType === 'supplier' && !isInternalTech;
	const focusedBudget = !isExternal ? nullSafeGetOrElse('workOrder.budgets.0', quote, {}) : {};
	const locationToSupplierRate = nullSafeGetOrElse(
		'workOrder.currencyExchangeRateLocationToSupplier',
		quote,
		1
	);
	const amount = nullSafeGetOrElse('totalAfterTax', quote, 0) / locationToSupplierRate;

	const { budget, budgetConsumed, thresholdPercentage } = focusedBudget;
	const consumed = parseFloat(budgetConsumed);
	const threshold = (parseFloat(thresholdPercentage) / 100) * budget;
	const overPercentage = Math.abs(roundOffToDecimal(((consumed + amount) / budget) * 100 - 100, 0));
	const overBudgetAmount = consumed + amount - budget;
	const underBudgetAmount = budget - (consumed + amount);
	const isBudgetZero = budget === 0;

	const isExceedingBudget = consumed + amount > budget;
	const isExceedingThreshold = consumed + amount > threshold && !isExceedingBudget;

	const hasAlert = isExceedingBudget || isExceedingThreshold;

	const budgetMessage = isExceedingBudget ? (
		<span>
			Approving this quote will put you{' '}
			<span style={{ color: STOPLIGHT_COLORS.pureRed }}>
				{isBudgetZero
					? locationCurrency.format(overBudgetAmount)
					: `${overPercentage}% (${locationCurrency.format(overBudgetAmount)})`}
			</span>{' '}
			over budget. Continue?
		</span>
	) : isExceedingThreshold ? (
		<span>
			Approving this quote will put you{' '}
			<span style={{ color: '#D79128' }}>
				{isBudgetZero
					? locationCurrency.format(Math.abs(underBudgetAmount))
					: `${overPercentage}% (${locationCurrency.format(underBudgetAmount)})`}
			</span>{' '}
			from your budget limit. Continue?
		</span>
	) : undefined;

	const showAlert = hasAlert && compareStrings(currentQuote.status, 'pending');

	// When the quote originated from a completed (awarded) RFP, surface that RFP context here instead
	// of the generic approval message. Gate on the quote's own RFP link (requestForProposalId), not
	// the work order's RFP history — a work order can carry unrelated/stale rfpDataIds.
	const awardedThroughRFP =
		nullSafeGet('requestForProposalId', currentQuote) &&
		compareStrings(nullSafeGet('requestForProposal.status', currentQuote), 'award');
	// A declined RFP quote only means "not accepted through the RFP" when its supplier actually lost
	// the work order to another bidder. If this quote's supplier still holds the work order, the
	// decline is a normal dispute, so keep the standard decline messaging.
	const notAcceptedThroughRFP =
		awardedThroughRFP &&
		nullSafeGet('supplierFacilityId', currentQuote) !==
			nullSafeGet('workOrder.supplierFacilityId', currentQuote);

	const quoteString =
		currentQuote && currentQuote.status
			? compareStrings(currentQuote.status, 'pending')
				? showAlert && allowApproval
					? budgetMessage
					: 'This quote is on hold until it has been fully approved'
				: compareStrings(currentQuote.status, 'awarded')
				? awardedThroughRFP
					? 'This quote was awarded through a RFP.'
					: 'This quote has been approved'
				: compareStrings(currentQuote.status, 'declined')
				? notAcceptedThroughRFP
					? 'This quote was not accepted through the RFP.'
					: 'This quote has been declined'
				: compareStrings(currentQuote.status, QUOTE_STATUSES.draft)
				? 'This quote is still a draft'
				: 'This quote has been voided'
			: null;

	const approvalForwardingEnabled =
		userType === 'supplier'
			? _.get(
					currentUser,
					'facility.privateBuyerCompanySettings.config.approvalHierarchyConfig.addUserToProposalApprovalHierarchyOnTheGo',
					true
			  )
			: _.get(
					companyConfig,
					'config.approvalHierarchyConfig.addUserToProposalApprovalHierarchyOnTheGo',
					true
			  );
	//Has approval hierarchy and is in it, or there is no approval hierarchy and therefore can approve
	const isInHierarchyOrNoHierarchy =
		(hasApprovalHierarchy && myTier !== undefined) || !hasApprovalHierarchy;
	const showForwardForApproval =
		isInHierarchyOrNoHierarchy && status === 'pending' && approvalForwardingEnabled;

	const approvalForwardDetails = nullSafeGetOrElse('approvalForwardedDetails', quote, []);
	const isForwardForApprovalDisabled = useMemo(() => {
		if (!compareStrings(status, 'awarded')) {
			if (compareStrings(nullSafeGet('workOrder.displayStatus', quote), 'Pending')) {
				setReason(`Work Order is still pending, quote can't be processed yet.`);
				return true;
			}
			return !isInHierarchyOrNoHierarchy;
		}
	}, [hasApprovalHierarchy, myTier, status, quote]);

	const buttons = (
		<Row>
			{canShowDraftStatus &&
			compareStrings(quote.status, QUOTE_STATUSES.pending) &&
			isThirdPartySupplier(currentUser) ? (
				<QuoteMoveBackToDraftButton
					setActionButtonLoading={setActionButtonLoading}
					actionButtonLoading={actionButtonLoading}
					markDraftQuote={markDraftQuote}
					quote={currentQuote}
					refresh={refresh}
				/>
			) : null}
			{isThirdPartySupplier(currentUser) &&
			!canShowPayingSupplierApprove &&
			canMoveVoidedQuoteToPending ? (
				<Button
					key="unvoid"
					loading={actionButtonLoading === QUOTE_STATUSES.pending}
					disabled={disabledByAnotherAction(actionButtonLoading, QUOTE_STATUSES.pending)}
					onClick={() => handleMoveBackToPendingQuote(currentQuote)}
				>
					Move back to pending
				</Button>
			) : null}
			{canShowPayingSupplierApprove ? (
				<div
					style={{
						display: 'inline-flex',
						position: 'relative',
					}}
				>
					<div style={{ gap: '16px', alignContent: 'flex-start' }} className={'flex flex-wrap'}>
						{quote.status === QUOTE_STATUSES.pending || quote.status === QUOTE_STATUSES.disputed ? (
							<Button
								type="primary"
								key="approve"
								onClick={() => {
									const fn = isSubcontractorView ? approveQuoteContractor : approveQuote;
									setActionButtonLoading(QUOTE_STATUSES.approved);
									fn(currentQuote)
										.then(() => {
											refresh();
										})
										.finally(() => {
											setActionButtonLoading(null);
										});
								}}
								loading={actionButtonLoading === QUOTE_STATUSES.approved}
								disabled={disabledByAnotherAction(actionButtonLoading, QUOTE_STATUSES.approved)}
							>
								Approve Quote
							</Button>
						) : null}
						{quote.status === QUOTE_STATUSES.pending ? (
							<Button
								loading={actionButtonLoading === QUOTE_STATUSES.disputed}
								disabled={disabledByAnotherAction(actionButtonLoading, QUOTE_STATUSES.disputed)}
								style={{ marginRight: 12 }}
								onClick={() => {
									const fn = isSubcontractorView ? declineQuoteContractor : declineQuote;
									setActionButtonLoading(QUOTE_STATUSES.disputed);
									fn(quote)
										.then(() => {
											refresh();
										})
										.finally(() => {
											setActionButtonLoading(null);
										});
								}}
							>
								Decline Quote
							</Button>
						) : null}
						{quote.status === QUOTE_STATUSES.approved || canMoveVoidedQuoteToPending ? (
							<Button
								key="approve"
								onClick={() => {
									const fn = isSubcontractorView ? pendingQuoteContractor : markPendingQuote;
									setActionButtonLoading(QUOTE_STATUSES.pending);
									fn(currentQuote)
										.then(() => {
											refresh();
										})
										.finally(() => {
											setActionButtonLoading(null);
										});
								}}
								loading={actionButtonLoading === QUOTE_STATUSES.pending}
								disabled={disabledByAnotherAction(actionButtonLoading, QUOTE_STATUSES.pending)}
							>
								Move back to pending
							</Button>
						) : null}
					</div>
				</div>
			) : null}
			{allowedToApprove ? (
				<div
					style={{
						display: 'inline-flex',
						position: 'relative',
					}}
				>
					<div style={{ gap: '16px', alignContent: 'flex-start' }} className={'flex flex-wrap'}>
						{showApproval ? (
							allowApproval ? (
								<Button
									type="primary"
									key="approve"
									loading={actionButtonLoading === QUOTE_STATUSES.approved}
									disabled={disabledByAnotherAction(actionButtonLoading, QUOTE_STATUSES.approved)}
									onClick={() => {
										handleApproveQuote(currentQuote);
									}}
								>
									Approve Quote
								</Button>
							) : (
								<Popover title={`Why can't I approve?`} content={nullSafeGet('current', reason)}>
									<Button type="primary" key="approve" disabled={true}>
										Approve Quote
									</Button>
								</Popover>
							)
						) : null}
						{showApproval ? (
							allowDecline ? (
								<Button
									key="reject"
									loading={actionButtonLoading === QUOTE_STATUSES.disputed}
									disabled={disabledByAnotherAction(actionButtonLoading, QUOTE_STATUSES.disputed)}
									onClick={() => {
										handleDisputeQuote(currentQuote);
									}}
								>
									Decline Quote
								</Button>
							) : (
								<Popover title={`Why can't I decline?`} content={nullSafeGet('current', reason)}>
									<Button key="reject" disabled={true}>
										Decline Quote
									</Button>
								</Popover>
							)
						) : null}
						{showForwardForApproval ? (
							<ForwardApproval
								disabled={isForwardForApprovalDisabled}
								reason={reason}
								workOrder={quote.workOrder}
								entity={quote}
								refresh={refresh}
								forwardApprovalToUser={forwardApprovalToUser}
								actionButtonLoading={actionButtonLoading}
							/>
						) : null}
						{compareStrings(nullSafeGet('status', currentQuote), QUOTE_STATUSES.approved) ||
						canMoveVoidedQuoteToPending ? (
							<Button
								key="approve"
								loading={actionButtonLoading === QUOTE_STATUSES.pending}
								disabled={disabledByAnotherAction(actionButtonLoading, QUOTE_STATUSES.pending)}
								onClick={() => handleMoveBackToPendingQuote(currentQuote)}
							>
								Move Back to Pending
							</Button>
						) : null}
						{hasApprovalHierarchy || hasApprovers(tiers) ? (
							<div
								className={'inline-flex flex-row items-center'}
								style={{ cursor: 'pointer', fontSize: '16px' }}
								onClick={() => setShowModal(true)}
							>
								<i className="icons8-font icons8-multiple-users" />
								<span style={{ marginLeft: '8px', textDecoration: 'underline' }}>
									{showStatus(status)}
								</span>
							</div>
						) : null}
					</div>
				</div>
			) : null}
		</Row>
	);

	return canShowDraftStatus && compareStrings(quote.status, QUOTE_STATUSES.draft) ? (
		<PublishQuoteBanner
			actionButtonLoading={actionButtonLoading}
			setActionButtonLoading={setActionButtonLoading}
			markPendingQuote={markPendingQuote}
			quote={currentQuote}
			refresh={refresh}
		/>
	) : (
		<Col style={{ flex: 1 }}>
			<Row className={'justify-between'}>
				<Col>
					<Row>
						<DeclineQuoteForm
							visible={showDeclineModal}
							onCancel={() => setShowDeclineModal(false)}
							onSubmit={handleSubmitDisputeQuote}
						/>
						<ApprovalHierarchyDisplayQuoteModal
							isNTEBased={isNTEBased}
							tiers={tiers}
							hasApprovalHierarchy={hasApprovalHierarchy}
							currentQuote={currentQuote}
							setVisible={setShowModal}
							visible={showModal}
							style={style}
							exchangeRate={exchangeRate}
							currency={currency}
							currentUser={currentUser}
							refresh={refresh}
							syncQuote={syncQuote}
						/>
						{showAlert && allowApproval ? (
							<div>
								<div className="flex flex-row items-baseline">
									<h5>{quoteString}</h5>
									<ViewPastRevisionsButton record={quote} recordType={'quote'} />
								</div>
							</div>
						) : (
							<div className="flex flex-row items-baseline">
								<h5 style={{ display: 'flex' }}>{quoteString}</h5>
								<ViewPastRevisionsButton record={quote} recordType={'quote'} />
							</div>
						)}
					</Row>
					<Row>{buttons}</Row>
					<Row className={status === 'declined' && declineNote ? 'mt-4' : ''}>
						{status === 'declined' ? declineNote : null}
					</Row>
					{nullSafeGetOrElse('length', approvalForwardDetails, 0) > 0 ? (
						<Row className={'mb-2'}>
							<span>
								Approval has been forwarded to {` `}
								{approvalForwardDetails.map((approvalForwarded, i) => {
									const approvalForwardedToContact = nullSafeGet(
										'forwardedToContact',
										approvalForwarded
									);
									const forwardedAt = nullSafeGet('forwardedAt', approvalForwarded);
									const forwardedByContact = nullSafeGet('forwardedByContact', approvalForwarded);
									return (
										<span>
											<UserName
												contact={approvalForwardedToContact}
												mode={'inline'}
												placement={'top'}
												extraDetails={[
													{
														key: 'forwardedAt',
														display: 'Forwarded',
														value: (
															<DateTimeHover
																timestamp={forwardedAt}
																timeZone={getWorkOrderTimeZoneId(workOrder)}
															/>
														),
													},
													{
														key: 'forwardedBy',
														display: 'Forwarded By',
														value: <UserName contact={forwardedByContact} mode={'inline'} />,
													},
												]}
											/>
											{joinListElements(approvalForwardDetails.length, i)}
										</span>
									);
								})}
							</span>
						</Row>
					) : null}
				</Col>
			</Row>
			{focusedBudget && hasAlert && allowApproval ? (
				<Row style={{ width: '65%', flex: 1, minWidth: 400 }}>
					<BudgetChart
						currency={locationCurrency}
						data={[
							{
								budget: focusedBudget.title,
								'Budget Used': consumed,
								'This Quote': amount,
								'Remaining Budget': budget - (consumed + amount),
							},
						]}
						consumedAmount={consumed}
						remainingAmount={budget - (consumed + amount)}
						itemAmount={amount}
						consumed={consumed > 0}
						remaining={budget - (consumed + amount) > 0}
						budgetTotal={budget}
						recordType={'Quote'}
						threshold={threshold}
					/>
				</Row>
			) : null}
		</Col>
	);
}

const mapStateToProps = (state, ownProps) => ({
	history: ownProps.history,
});
const mapDispatchToProps = (dispatch, ownProps) => ({
	forwardApprovalToUser: (entity, email) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? forwardApprovalToUserForSupplier('proposal', entity, email)
				: forwardApprovalToUserForBuyer('proposal', entity, email)
		),
	approveQuoteContractor: (entity) =>
		dispatch(proposalStatusChangeForContractor('awarded', entity)),
	pendingQuoteContractor: (entity) =>
		dispatch(proposalStatusChangeForContractor('pending', entity)),
	declineQuoteContractor: (entity) =>
		dispatch(proposalStatusChangeForContractor('declined', entity)),
	markDraftQuote: (entity, isContractorQuote = false) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? isInternalTech(ownProps.currentUser)
					? proposalStatusChangeForInternalSupplier('draft', entity)
					: isContractorQuote
					? proposalStatusChangeForClientContractor('draft', entity)
					: proposalStatusChangeForSupplier('draft', entity)
				: proposalStatusChangeForBuyer('draft', entity)
		),
	markPendingQuote: (entity, isContractorQuote = false) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? isInternalTech(ownProps.currentUser)
					? proposalStatusChangeForInternalSupplier('pending', entity)
					: isContractorQuote
					? proposalStatusChangeForClientContractor('pending', entity)
					: proposalStatusChangeForSupplier('pending', entity)
				: proposalStatusChangeForBuyer('pending', entity)
		),
});
const ComponentWithoutUserType = connect(
	mapStateToProps,
	mapDispatchToProps
)(ApprovalHierarchyDisplayQuote);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
		companyConfig: (state as any).company_config.detail,
		currentUser: (state as any).session.currentUser,
	}),
	() => ({})
)(ComponentWithoutUserType);
